// usePayrollEmployeeDetails.js

import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { getPayrollEmployeeDetails, getPayrollItemNames, GetPayrollDetails } from "../../api";
import { formatAmount, extractEmployeePayrollDetails, fetchAISuggestions, applyAISuggestions, cleanFrequency } from "../../HelperFunctions/PayrollEntryHelpers";
import { ITEM_TYPE } from "../../Enums/Enum";
import { useParams } from "react-router-dom";

export const usePayrollEmployeeDetails = ({
  row,
  values,
  corporationId,
  setReloadTrigger,
  departments,
  jobTitles,
  empPayrollDetails,
  setAskAiData,

  setLoadingDetails,
  setFormValues,
  setEmpPayrollDetails,
  setPayrollItems,

  setIsVoid,
  setVoidDate,
  setComments,

  setSelectedEmployeeId,
  setDisplayName,
  setIsDropdownSelected,
  setManualDeptJobChange,

  setAskAiButtonVisible,
  setAskAiPopupOpen,

  setFieldValue,
  skipAskAiOnEmployeeChangeRef,

  displayHours,
  apiRatesRef,
  originalFormValuesRef,
  setInitialState,
  skipPayrollDetailsLoad: propSkipPayrollDetailsLoad,
  isReportScreen: propIsReportScreen,
  isCompletedRecord: propIsCompletedRecord,
}) => {

  const { payrollEmpId, popupID } = useParams();
  const lastFetchedPrDetailsKeyRef = useRef(null);
  const lastFetchedPrDetailsResultRef = useRef(null);

  useEffect(() => {
    const fetchDetails = async () => {
      const empId = row?.raw?.PayrollEmpID || row?.raw?.PayrollEmpId || payrollEmpId;
      if (empId) {
        if (lastFetchedPrDetailsKeyRef.current === String(empId)) {
          return;
        }
        lastFetchedPrDetailsKeyRef.current = String(empId);
        setLoadingDetails(true);
        try {
          const data = await getPayrollEmployeeDetails(empId);
          if (data) {
            const employeeName = data.EmployeeName || "";
            setFieldValue("corporationId", data.CorporationID);
            setFieldValue("payrollId", data.PayrollID);
            setFieldValue("account", data.BankAccountID);
            setFieldValue("IsHoursDecimal", data.IsHoursDecimal);
            setFieldValue("payrollEmpId", data.PayrollEmpId || data.PayrollEmpID);
            setFieldValue("importPayrollType", data.ImportPayrollType);
            setFieldValue("importedName", data.ImportedEmployeeName || "");
            setFieldValue("payDate", data.PayDate || values?.payDate || "");
            setFieldValue("FrequencyID", data.FrequencyID || "");
            setFieldValue("frequencyId", data.FrequencyID || values?.frequencyId || "");
            setFieldValue("payFrequency", data.PayFrequency || data.PayFrequencyDate || values?.payFrequency || "");
            const employeeId = data.EmployeeID || data.EmployeeId || row?.raw?.EmployeeID || row?.raw?.EmployeeId || null;
            let deptId = data.DepartmentID || data.DepartmentId || data.IncDeptID || data.IncDeptId || row?.raw?.DepartmentID || row?.raw?.DepartmentId || row?.raw?.IncDeptID || row?.raw?.IncDeptId || "";
            if (!deptId && (departments || []).length > 0) {
              const deptName = data.DepartmentName || row?.raw?.DepartmentName;
              if (deptName) {
                const match = departments.find(d => String(d.label).trim().toLowerCase() === String(deptName).trim().toLowerCase());
                if (match) {
                  deptId = match.value;
                }
              }
            }
            const jobId = data.JobID || data.JobId || data.JobTitleID || data.JobTitleId || row?.raw?.JobID || row?.raw?.JobId || "";
            const pcIdVal = data.PCID || data.ProfitCenterID || row?.raw?.PCID || row?.raw?.ProfitCenterID || "";
            const corpIdVal = data.CorporationID || data.CorporationId || corporationId || values?.corporationId || values?.corpId || row?.raw?.CorporationID || row?.raw?.CorporationId || "";
            setFormValues((prev) => ({
              ...prev,
              name: employeeName,
              department: deptId,
              job: jobId,
              checkNo: data.CheckNum || "",
              netPay: formatAmount(data.NetPay || 0),
              profitCenter: pcIdVal,
              employeeId: employeeId,
              isVoidStamp: data.IsActive === 2,
              payrollFilePath: data.PayrollFilePath,
              reconStatus: data.ReconStatus || false,
              clearedDate: data.ClearedDate || null,
              importedName: data.ImportedEmployeeName || "",
              importedDept: data.ImportedDeptName || "",
              importedJob: data.ImportedJobName || "",
              importedNetPay: data.ImportedNetPay !== null && data.ImportedNetPay !== undefined ? formatAmount(data.ImportedNetPay) : "",
              corporationName: data.CorporationName || "",
              payFrequency: data.PayFrequency || "",
              payDate: data.PayDate || null,
              bankAccountID: data.BankAccountID || "",
              bankAccount: data.BankAccount || "",
              corporationID: corpIdVal,
              frequencyID: data.FrequencyID || "",
            }));
            const hasInitialDept = Boolean(
              (deptId && String(deptId).trim() !== "" && String(deptId).toLowerCase() !== "null") ||
              (data?.DepartmentID && String(data.DepartmentID).trim() !== "" && String(data.DepartmentID).toLowerCase() !== "null") ||
              (data?.IncDeptID && String(data.IncDeptID).trim() !== "" && String(data.IncDeptID).toLowerCase() !== "null") ||
              (data?.DepartmentName && String(data.DepartmentName).trim() !== "" && String(data.DepartmentName).toLowerCase() !== "null")
            );

            const hasInitialJob = Boolean(
              (jobId && String(jobId).trim() !== "" && String(jobId).toLowerCase() !== "null") ||
              (data?.JobID && String(data.JobID).trim() !== "" && String(data.JobID).toLowerCase() !== "null") ||
              (data?.JobTitleID && String(data.JobTitleID).trim() !== "" && String(data.JobTitleID).toLowerCase() !== "null") ||
              (data?.JobTitle && String(data.JobTitle).trim() !== "" && String(data.JobTitle).toLowerCase() !== "null") ||
              (data?.JobName && String(data.JobName).trim() !== "" && String(data.JobName).toLowerCase() !== "null")
            );

            const isInitialDeptJobNull = !hasInitialDept && !hasInitialJob;

            originalFormValuesRef.current = {
              department: deptId,
              job: jobId,
              profitCenter: pcIdVal,
              hasInitialDept,
              hasInitialJob,
              isInitialDeptJobNull,
            };
            setIsVoid(data.IsVoided === true);
            const parsedVoidDate = data.VoidDate ? dayjs(data.VoidDate) : null;
            if (parsedVoidDate && parsedVoidDate.year() > 2000) {
              setVoidDate(parsedVoidDate);
            } else {
              const defaultPayDate = data.PayDate || values?.payDate;
              setVoidDate(defaultPayDate && dayjs(defaultPayDate).isValid() ? dayjs(defaultPayDate) : dayjs());
            }
            setComments(data.VoidRemarks || "");
            setSelectedEmployeeId(employeeId);
            setDisplayName(employeeName);

            const isMatchedRecord = row?.statusText === "Matched" || row?.view === "Matched" || !!employeeName;
            setIsDropdownSelected(isMatchedRecord);
            setManualDeptJobChange(0);
            setEmpPayrollDetails(data.EmpPayrollDetails || []);

            const prNamesPayload = {
              CorpID: data.CorporationID,
              itemTypes: [ITEM_TYPE.Wages, ITEM_TYPE.Additions, ITEM_TYPE.Deductions, ITEM_TYPE.EmployeeTaxes, ITEM_TYPE.EmployerTaxes]
            };

            const payrollNames = await getPayrollItemNames(prNamesPayload);

            const itemsList = payrollNames?.Items || [];
            setPayrollItems(itemsList);

            const { wagesData, apiRates, addDedData, taxesData } = extractEmployeePayrollDetails(data, displayHours, values?.IsHoursDecimal);

            apiRatesRef.current = apiRates;

            const isMatchedStatus = row?.statusText === "Matched" || row?.view === "Matched" || row?.statusText === "Completed" || row?.view === "Completed";
            const departmentExists = departments.some(
              d => d.value === data.DepartmentID
            );

            const jobExists = jobTitles.some(
              j => j.value === data.JobID
            );

            const importedEmpName = data.ImportedEmployeeName ?? row?.raw?.ImportedEmployeeName ?? row?.importedName ?? values?.importedName;
            const isImportedNameEmpty = !importedEmpName || String(importedEmpName).trim() === "" || String(importedEmpName).toLowerCase() === "null" || String(importedEmpName).toLowerCase() === "undefined";
            const isType1 = Boolean(
              isImportedNameEmpty ||
              values?.importPayrollType === 1 ||
              values?.isAddPayRoll ||
              values?.empType === 1 ||
              values?.EmpType === 1 ||
              row?.isAddPayRoll ||
              row?.raw?.ImportPayrollType === 1 ||
              row?.importPayrollType === 1 ||
              String(row?.entryType || "").toUpperCase() === "MANUAL" ||
              String(row?.raw?.EntryType || "").toUpperCase() === "MANUAL" ||
              data?.ImportPayrollType === 1
            );

            const isReportScreen = Boolean(
              propIsReportScreen ||
              popupID ||
              payrollEmpId ||
              row?.skipPayrollDetailsLoad ||
              row?.isReport ||
              row?.fromReport
            );

            const isCompletedRecord = Boolean(
              propIsCompletedRecord ||
              propSkipPayrollDetailsLoad ||
              row?.skipPayrollDetailsLoad ||
              row?.statusText === "Matched" ||
              row?.statusText === "Completed" ||
              row?.view === "Matched" ||
              row?.view === "Completed" ||
              String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "matched" ||
              String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "completed" ||
              String(data?.MappingStatus || "").trim().toLowerCase() === "matched" ||
              String(data?.MappingStatus || "").trim().toLowerCase() === "completed" ||
              row?.isCompleted ||
              row?.raw?.IsCompleted
            );

            const isUnmappedRecord = !isReportScreen && !isCompletedRecord && Boolean(
              row?.view === "Unmapped" ||
              row?.statusText === "Unmapped" ||
              String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "unmapped" ||
              row?.isNewEmployeeJustAdded ||
              row?.isUnmapped ||
              !row?.raw?.MappingStatus
            );

            if (originalFormValuesRef?.current) {
              originalFormValuesRef.current.isUnmappedRecord = isUnmappedRecord;
            }

            if (!skipAskAiOnEmployeeChangeRef.current && !isType1 && !isMatchedStatus && !!data.DepartmentID && !!data.JobID && departmentExists && jobExists) {
              try {
                const payload = {
                  CorpID: data.CorporationID,
                  PayrollEmpID: String(data.PayrollEmpId || data.PayrollEmpID || empId),
                  DepartmentName: data.DepartmentName,
                  JobName: data.JobTitle,
                  DepartmentID: data.DepartmentID,
                  JobID: data.JobID,
                  IsDeptJob: false
                };
                const { dataList, manualItems, autoMapItems } = await fetchAISuggestions(payload);

                if (autoMapItems && autoMapItems.length > 0) {
                  const result = applyAISuggestions({
                    suggestions: autoMapItems,
                    wages: wagesData,
                    addDeds: addDedData,
                    taxes: taxesData,
                    displayHours,
                    isHoursDecimal: values?.IsHoursDecimal,
                    overwriteExisting: false,
                    clearCOA: false,
                    payrollItems: itemsList
                  });

                  wagesData.splice(
                    0,
                    wagesData.length,
                    ...result.wages
                  );

                  addDedData.splice(
                    0,
                    addDedData.length,
                    ...result.addDeds
                  );

                  taxesData.splice(
                    0,
                    taxesData.length,
                    ...result.taxes
                  );
                }

                if (manualItems.length === 0) {
                  setAskAiButtonVisible(false);
                } else {
                  setAskAiData(dataList);
                  setAskAiButtonVisible(true);
                  setAskAiPopupOpen(true);
                }
              } catch (error) {
                console.error("Error fetching initial AI suggestions:", error);
                setAskAiButtonVisible(false);
              }
            } else {
              setAskAiButtonVisible(false);
            }

            if (!isReportScreen && !isCompletedRecord && isUnmappedRecord && isType1 && wagesData.length === 0 && (deptId || jobId)) {
              try {
                const prDetailsPayload = {
                  CorpID: corpIdVal,
                  IncDeptID: deptId,
                  JobId: jobId || "",
                  EmployeeId: employeeId,
                  ImportPayrollType: 1,
                  Frequency: cleanFrequency(data?.PayFrequency || values?.payFrequency || values?.frequency || ""),
                };
                if (pcIdVal) {
                  prDetailsPayload.PCID = pcIdVal;
                }
                const prPayloadKey = JSON.stringify(prDetailsPayload);
                let details;
                if (lastFetchedPrDetailsKeyRef.current === prPayloadKey && lastFetchedPrDetailsResultRef.current) {
                  details = lastFetchedPrDetailsResultRef.current;
                } else {
                  lastFetchedPrDetailsKeyRef.current = prPayloadKey;
                  details = await GetPayrollDetails(prDetailsPayload);
                  lastFetchedPrDetailsResultRef.current = details;
                }
                const rawList = Array.isArray(details)
                  ? details
                  : (details?.EmpPayrollItemDetails || []);
                const prItems = Array.isArray(rawList) ? rawList : [];

                if (prItems.length > 0) {
                  const wageItems = prItems.filter(item => {
                    const type = Number(item.Type ?? item.type ?? item.ItemType ?? item.itemType);
                    return type === ITEM_TYPE.Wages || type === null || type === 0 || isNaN(type);
                  });
                  const addDedItems = prItems.filter(item => {
                    const type = Number(item.Type ?? item.type ?? item.ItemType ?? item.itemType);
                    return type === ITEM_TYPE.Additions || type === ITEM_TYPE.Deductions;
                  });
                  const taxItems = prItems.filter(item => {
                    const type = Number(item.Type ?? item.type ?? item.ItemType ?? item.itemType);
                    return type === ITEM_TYPE.EmployeeTaxes || type === ITEM_TYPE.EmployerTaxes;
                  });

                  if (wageItems.length > 0) {
                    wagesData.length = 0;
                    wageItems.forEach((item, idx) => {
                      const mappedToVal = item.PayrollItemID || item.ItemID || item.PayrollItemId || item.itemId || item.Id || "";
                      const mappedToLabel = item.PayrollItemName || item.ItemName || item.itemName || "";
                      const coaVal = item.AccountID || item.COA || item.AccountId || item.coa || "";
                      const itemRate = item.Rate ?? item.rate;
                      const rateVal = (itemRate !== null && itemRate !== undefined && String(itemRate).trim() !== "") ? Number(itemRate).toFixed(2) : "0.00";
                      const itemAmt = item.Amount ?? item.amount;
                      const grossVal = (itemAmt !== null && itemAmt !== undefined && String(itemAmt).trim() !== "") ? Number(itemAmt).toFixed(2) : "0.00";
                      wagesData.push({
                        id: Date.now() + idx,
                        wageName: mappedToLabel,
                        mappedTo: mappedToVal,
                        PayrollItemId: mappedToVal,
                        mappedToName: mappedToLabel,
                        coa: coaVal,
                        hrs: values?.IsHoursDecimal ? "0.00" : "0:00",
                        rate: rateVal,
                        gross: grossVal,
                      });
                    });
                  }

                  if (addDedItems.length > 0) {
                    addDedData.length = 0;
                    addDedItems.forEach((item, idx) => {
                      const mappedToVal = item.PayrollItemID || item.ItemID || item.PayrollItemId || item.itemId || item.Id || "";
                      const mappedToLabel = item.PayrollItemName || item.ItemName || item.itemName || "";
                      const coaVal = item.AccountID || item.COA || item.AccountId || item.coa || "";
                      const itemAmt = item.Amount ?? item.amount;
                      const amtVal = (itemAmt !== null && itemAmt !== undefined && String(itemAmt).trim() !== "") ? Number(itemAmt).toFixed(2) : "0.00";
                      const type = Number(item.Type ?? item.type ?? item.ItemType ?? item.itemType);
                      addDedData.push({
                        id: Date.now() + 100 + idx,
                        addDeduction: type || ITEM_TYPE.Additions,
                        item: mappedToLabel,
                        mappedTo: mappedToVal,
                        mappedToName: mappedToLabel,
                        coa: coaVal,
                        amount: amtVal,
                      });
                    });
                  }

                  if (taxItems.length > 0) {
                    taxesData.length = 0;
                    taxItems.forEach((item, idx) => {
                      const mappedToVal = item.PayrollItemID || item.ItemID || item.PayrollItemId || item.itemId || item.Id || "";
                      const mappedToLabel = item.PayrollItemName || item.ItemName || item.itemName || "";
                      const coaVal = item.AccountID || item.COA || item.AccountId || item.coa || "";
                      const expCoa = item.ExpenseAccountID || item.ExpenseCOA || item.expenseCoa || "";
                      const itemAmt = item.Amount ?? item.amount;
                      const amtVal = (itemAmt !== null && itemAmt !== undefined && String(itemAmt).trim() !== "") ? Number(itemAmt).toFixed(2) : "0.00";
                      const type = Number(item.Type ?? item.type ?? item.ItemType ?? item.itemType);
                      taxesData.push({
                        id: Date.now() + 200 + idx,
                        taxType: type || ITEM_TYPE.EmployeeTaxes,
                        item: mappedToLabel,
                        mappedTo: mappedToVal,
                        mappedToName: mappedToLabel,
                        coa: coaVal,
                        expenseCoa: expCoa,
                        amount: amtVal,
                      });
                    });
                  }
                }
              } catch (err) {
                console.error("Error fetching default payroll details for manual employee:", err);
              }
            }

            const currentWageHasItems = (values?.wage || []).some(w => w.wageName || w.mappedTo);
            const initialWages = wagesData.length > 0
              ? wagesData
              : (currentWageHasItems ? values.wage : [{ wageName: "", mappedTo: "", mappedToName: "", coa: "", hrs: values?.IsHoursDecimal ? "0.00" : "0:00", rate: "0.00", gross: "0.00" }]);

            const currentAddDedHasItems = (values?.addAndDeductions || []).some(a => a.item || a.mappedTo);
            const initialAddDeds = addDedData.length > 0
              ? addDedData
              : (currentAddDedHasItems ? values.addAndDeductions : [{ id: Date.now(), addDeduction: "", item: "", mappedTo: "", coa: "", coaName: "", amount: "0.00" }]);

            const currentTaxesHasItems = (values?.eeAndErTaxes || []).some(t => t.item || t.mappedTo);
            const initialTaxes = taxesData.length > 0
              ? taxesData
              : (currentTaxesHasItems ? values.eeAndErTaxes : [{ id: Date.now() + 1, taxType: "", item: "", mappedTo: "", coa: "", coaName: "", expenseCoa: "", amount: "0.00" }]);

            setFieldValue('wage', initialWages);
            setFieldValue('addAndDeductions', initialAddDeds);
            setFieldValue('eeAndErTaxes', initialTaxes);

            setInitialState?.({
              formValues: {
                name: employeeName,
                department: deptId,
                job: jobId,
                checkNo: data.CheckNum || "",
                profitCenter: pcIdVal,
                employeeId: employeeId,
                account: data.BankAccountID || "",
              },
              values: {
                wage: initialWages,
                addAndDeductions: initialAddDeds,
                eeAndErTaxes: initialTaxes,
                account: data.BankAccountID || "",
              }
            });

            setTimeout(() => {
              setReloadTrigger?.(Date.now());
            }, 0);
          }
        } catch (error) {
          console.error("Failed to fetch employee payroll details", error);
        } finally {
          setLoadingDetails(false);
        }
      }
    };
    fetchDetails();
  }, [row, payrollEmpId]);

  useEffect(() => {
    const triggerInitialAiCheck = async () => {
      if (skipAskAiOnEmployeeChangeRef.current) {
        return;
      }

      const empId = row?.raw?.PayrollEmpID || row?.raw?.PayrollEmpId || payrollEmpId;
      if (!empId || !originalFormValuesRef.current.department || !originalFormValuesRef.current.job || departments.length === 0 || jobTitles.length === 0) {
        return;
      }

      const isMatchedStatus = row?.statusText === "Matched" || row?.view === "Matched" || row?.statusText === "Completed" || row?.view === "Completed";
      const importedEmpName = row?.raw?.ImportedEmployeeName ?? row?.importedName ?? values?.importedName;
      const isImportedNameEmpty = !importedEmpName || String(importedEmpName).trim() === "" || String(importedEmpName).toLowerCase() === "null" || String(importedEmpName).toLowerCase() === "undefined";
      const isType1 = Boolean(
        isImportedNameEmpty ||
        values?.importPayrollType === 1 ||
        values?.isAddPayRoll ||
        values?.empType === 1 ||
        values?.EmpType === 1 ||
        row?.isAddPayRoll ||
        row?.raw?.ImportPayrollType === 1 ||
        row?.importPayrollType === 1 ||
        String(row?.entryType || "").toUpperCase() === "MANUAL" ||
        String(row?.raw?.EntryType || "").toUpperCase() === "MANUAL"
      );
      if (isType1 || isMatchedStatus) {
        return;
      }

      const departmentExists = departments.some(d => d.value === originalFormValuesRef.current.department);
      const jobExists = jobTitles.some(j => j.value === originalFormValuesRef.current.job);

      if (departmentExists && jobExists) {
        setLoadingDetails(true);
        try {
          const payload = { // Use the prop `corporationId` here as it's the initial context
            CorpID: values.corporationId || corporationId,
            PayrollEmpID: String(empId),
            DepartmentName: departments.find(d => d.value === originalFormValuesRef.current.department)?.label,
            JobName: jobTitles.find(j => j.value === originalFormValuesRef.current.job)?.label,
            DepartmentID: originalFormValuesRef.current.department,
            JobID: originalFormValuesRef.current.job,
            IsDeptJob: false
          };

          const { dataList, manualItems } = await fetchAISuggestions(payload);

          if (manualItems.length === 0) {
            setAskAiButtonVisible(false);
            if (dataList.length > 0) {
              const { wagesData, addDedData, taxesData } = extractEmployeePayrollDetails({ EmpPayrollDetails: empPayrollDetails }, displayHours, values?.IsHoursDecimal);
              const result = applyAISuggestions({
                suggestions: dataList,
                wages: wagesData,
                addDeds: addDedData,
                taxes: taxesData,
                displayHours,
                isHoursDecimal: values?.IsHoursDecimal
              });
              setFieldValue('wage', result.wages);
              setFieldValue('addAndDeductions', result.addDeds);
              setFieldValue('eeAndErTaxes', result.taxes);
            }
          } else {
            setAskAiData(dataList);
            setAskAiButtonVisible(true);
            setAskAiPopupOpen(true);
          }
        } catch (error) {
          console.error("Error fetching initial AI suggestions:", error);
          setAskAiButtonVisible(false);
        } finally {
          setLoadingDetails(false);
          // Allow Ask AI again for future department/job changes
          if (skipAskAiOnEmployeeChangeRef.current) {
            skipAskAiOnEmployeeChangeRef.current = false;
          }
        }
      }
    };

    triggerInitialAiCheck();
  }, [row, departments, jobTitles, empPayrollDetails, corporationId, values?.corporationId, values?.corpId, values?.importPayrollType, values?.isAddPayRoll, values?.IsHoursDecimal, displayHours, setFieldValue, setLoadingDetails, setAskAiButtonVisible, setAskAiPopupOpen, originalFormValuesRef, payrollEmpId]);

};